import { isDefined } from '@eih-netbroker/common';
import { ClipboardEvent, FC, KeyboardEvent, useCallback, useMemo, useRef } from 'react';
import { useLocale } from '../../../context/NbLocalizationContext';
import classes from './CodeInputField.module.scss';
import { CodeInputEvents } from './events';
import { CodeInputUtils } from './utils';

export type CodeInputFieldProps = {
  text?: string;
  index: number;
  isSelected: boolean;
  codeLength: number;
  secureTextEntry?: boolean;
  disabled?: boolean;
  onFocus: (e: CodeInputEvents.Focus) => void;
  onInput: (e: CodeInputEvents.Input) => void;
  onEdit: (e: CodeInputEvents.Edit) => void;
  onPaste: (e: CodeInputEvents.Paste) => void;
};

export const CodeInputField: FC<CodeInputFieldProps> = ({
  text,
  index,
  codeLength,
  secureTextEntry,
  disabled,
  onFocus,
  onEdit,
  onInput,
  onPaste,
}) => {
  const { translation, translateWithParams } = useLocale();
  const ariaLabel = translateWithParams(translation.codeInput.character, {
    index: (index + 1).toString(),
    codeLength: codeLength.toString(),
  });

  // This is used to prevent the use case when
  // 1. CTRL/CMD + v is pressed
  // 2. CTRL/CMD is released, but "v" is kept being held
  // 3. New keyDown events fired with "v" that adds one or more "v"-s
  const jammedKeyCode = useRef<string | null>(null);

  const handleFocus = useCallback(() => {
    onFocus({ index, char: text });
  }, [index, onFocus, text]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (CodeInputUtils.hasModifiers(e)) {
        jammedKeyCode.current = e.code;
      }
      if (!CodeInputUtils.hasModifiers(e) && e.code === jammedKeyCode.current) {
        return;
      }
      if (CodeInputUtils.isEditKey(e.key)) {
        onEdit({ index, key: e.key });
        return;
      }
      if (!CodeInputUtils.hasModifiers(e) && CodeInputUtils.INPUT_CHAR_REGEX.test(e.key)) {
        onInput({ index, char: e.key });
      }
    },
    [index, onEdit, onInput],
  );

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.code === jammedKeyCode.current) {
      jammedKeyCode.current = null;
    }
  }, []);

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const value = CodeInputUtils.sanitizeSmsCode(e.clipboardData.getData('text'), codeLength);
      // Only run when this precise CodeInputField is focused.
      if (e.currentTarget !== document.activeElement) {
        return;
      }
      if (!isDefined(value)) {
        return;
      }
      onPaste({ index, value });
    },
    [codeLength, index, onPaste],
  );

  const label = useMemo(() => {
    if (!isDefined(text) || text.length === 0) {
      return '';
    }
    return secureTextEntry ? text.replace(/./g, '*') : text;
  }, [secureTextEntry, text]);

  return (
    <div
      {...{ [CodeInputUtils.CODE_INPUT_TAG]: index }}
      tabIndex={0}
      aria-label={ariaLabel}
      role="textbox"
      onClick={disabled ? undefined : handleFocus}
      onFocus={disabled ? undefined : handleFocus}
      onKeyDown={disabled ? undefined : handleKeyDown}
      onKeyUp={disabled ? undefined : handleKeyUp}
      onPaste={disabled ? undefined : handlePaste}
      className={classes.codeInputField}
    >
      {label}
    </div>
  );
};
